import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useLoc } from "@/lib/loc";
import { Plus, Pencil, Trash2, Loader2, Send, Undo2 } from "lucide-react";
import { toast } from "sonner";

const statusStyle = {
  draft: "bg-white/10 text-white/70",
  review: "bg-amber-500/15 text-amber-400",
  published: "bg-emerald-500/15 text-emerald-400",
};
const transcodeStyle = {
  ready: "text-emerald-400",
  preparing: "text-amber-400",
  waiting: "text-amber-400",
  errored: "text-destructive",
  none: "text-muted-foreground",
};

export default function AdminVideos() {
  const { t } = useTranslation();
  const loc = useLoc();
  const qc = useQueryClient();

  const { data: videos, isLoading } = useQuery({ queryKey: ["admin-videos"], queryFn: async () => (await api.get("/admin/videos")).data });

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/videos/${id}/status`, { status });
      toast.success(status === "published" ? "Vidéo publiée" : "Statut mis à jour");
      qc.invalidateQueries({ queryKey: ["admin-videos"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Retirer cette vidéo ? (retrait immédiat)")) return;
    await api.delete(`/admin/videos/${id}`);
    toast.success("Vidéo retirée");
    qc.invalidateQueries({ queryKey: ["admin-videos"] });
  };

  if (isLoading) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight">{t("admin.videos")}</h1>
        <Link to="/admin/videos/new" data-testid="admin-new-video" className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[hsl(var(--primary))]/90">
          <Plus className="h-4 w-4" /> {t("admin.newVideo")}
        </Link>
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Titre</th>
              <th className="px-4 py-3">{t("admin.status")}</th>
              <th className="px-4 py-3">{t("admin.transcode")}</th>
              <th className="px-4 py-3">Vues</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((v) => (
              <tr key={v.id} className="border-b border-white/5 transition-colors hover:bg-white/5" data-testid={`admin-video-row-${v.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={v.thumbnail_url || "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='9'><rect width='16' height='9' fill='%23191920'/></svg>"} alt="" className="h-10 w-16 rounded object-cover" />
                    <div>
                      <p className="font-medium">{loc(v.title)}</p>
                      {v.is_sponsored && <span className="text-[10px] uppercase tracking-widest text-primary">Sponsorisé</span>}
                      {v.is_demo && <span className="ml-2 text-[10px] text-muted-foreground">[DÉMO]</span>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-1 text-xs ${statusStyle[v.status]}`}>{t(`admin.${v.status}`)}</span>
                </td>
                <td className={`px-4 py-3 text-xs font-medium ${transcodeStyle[v.transcode_status] || "text-muted-foreground"}`}>
                  {v.transcode_status}
                </td>
                <td className="px-4 py-3">{(v.views || 0).toLocaleString("fr-FR")}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {v.status !== "published" ? (
                      <button onClick={() => setStatus(v.id, "published")} data-testid={`publish-${v.id}`} title={t("admin.publish")} className="flex items-center gap-1 rounded-md bg-emerald-500/15 px-2.5 py-1.5 text-xs text-emerald-400 transition-colors hover:bg-emerald-500/25">
                        <Send className="h-3.5 w-3.5" /> {t("admin.publish")}
                      </button>
                    ) : (
                      <button onClick={() => setStatus(v.id, "draft")} data-testid={`unpublish-${v.id}`} title={t("admin.unpublish")} className="flex items-center gap-1 rounded-md bg-white/10 px-2.5 py-1.5 text-xs text-white/70 transition-colors hover:bg-white/20">
                        <Undo2 className="h-3.5 w-3.5" /> {t("admin.unpublish")}
                      </button>
                    )}
                    <Link to={`/admin/videos/${v.id}`} data-testid={`edit-${v.id}`} className="rounded-md bg-white/5 p-2 text-muted-foreground transition-colors hover:text-foreground">
                      <Pencil className="h-3.5 w-3.5" />
                    </Link>
                    <button onClick={() => del(v.id)} data-testid={`delete-${v.id}`} className="rounded-md bg-white/5 p-2 text-muted-foreground transition-colors hover:text-destructive">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
